import { Select as DesignSelect } from "@/components/ui/design-system";
import { PageHeader, FilterBar, Table, Avatar } from "@/components/ui/design-system";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "@/context/AuthContext";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { SkeletonTablica } from "@/components/ui/skeleton";

import { porukaDohvata } from "@/lib/greske";
import {
  dohvatiRadnike,
  punoIme,
  statusRadnika,
  type Radnik,
  type UpitRadnika,
} from "@/lib/radnici";
import {
  kriticanRedak,
  oznakaDozvole,
  oznakaLijecnickog,
  oznakaUskladenosti,
} from "@/lib/status";
import { cn } from "@/lib/utils";
import { dohvatiGradilista, type Gradiliste } from "@/lib/gradilista";

/**
 * The personnel list — `docs/dizajn/ekran-popis-zaposlenika.dc.html`.
 *
 * Rows are 56px with a line between them and the action written as a word at
 * the end, never a menu behind three dots: the person using this is looking
 * for one of eighty-five people and should not have to hunt for the verb.
 *
 * Nothing here assumes a field is present. The server sends only what this
 * caller may see, so every cell falls back to "—" rather than to `undefined`
 * leaking into the page.
 */

type Status = "aktivni" | "svi" | "bivsi" | "povijesni";

interface Odgovor {
  kljuc: string;
  radnici: Radnik[];
  ukupno: number;
}

export function PopisZaposlenikaPage() {
  const { can } = useAuth();
  // Results and errors carry the query they answer. Loading is then derived
  // rather than stored: with a flag, the moment between "filter changed" and
  // "request started" shows the previous page as if it were the answer to the
  // new question.
  const [odgovor, setOdgovor] = useState<Odgovor | null>(null);
  const [greska, setGreska] = useState<{ kljuc: string; poruka: string } | null>(
    null,
  );

  const [pretraga, setPretraga] = useState("");
  const [status, setStatus] = useState<Status>("aktivni");
  const [siteId, setSiteId] = useState("");
  const [sites, setSites] = useState<Gradiliste[]>([]);
  const [siteError, setSiteError] = useState<string | null>(null);
  const canReadSites = can("raspored", "read") && can("gradiliste", "read");
  useEffect(() => {
    if (!canReadSites) return;
    const controller = new AbortController();
    void dohvatiGradilista(false, controller.signal).then(values => { if (!controller.signal.aborted) setSites(values); }).catch(() => { if (!controller.signal.aborted) setSiteError("Popis gradilišta nije dostupan."); });
    return () => controller.abort();
  }, [canReadSites]);
  const [samoUpozorenja, setSamoUpozorenja] = useState(false);

  // The search box types faster than the network answers; without this every
  // keystroke is a request and the answers can arrive out of order.
  const [odgodenaPretraga, setOdgodenaPretraga] = useState("");

  const kljuc = `${odgodenaPretraga.trim()}|${status}|${siteId}`;
  const ucitava = odgovor?.kljuc !== kljuc && greska?.kljuc !== kljuc;
  const radnici = odgovor?.kljuc === kljuc ? odgovor.radnici : [];
  const ukupno = odgovor?.kljuc === kljuc ? odgovor.ukupno : 0;

  useEffect(() => {
    const odgoda = setTimeout(() => {
      setOdgodenaPretraga(pretraga);
    }, 250);
    return () => clearTimeout(odgoda);
  }, [pretraga]);

  useEffect(() => {
    const kontroler = new AbortController();

    const upit: UpitRadnika = {
      limit: 200,
      offset: 0,
      status,
    };
    if (odgodenaPretraga.trim()) upit.q = odgodenaPretraga.trim();
    if (siteId) upit.gradiliste_id = siteId;

    dohvatiRadnike(upit, kontroler.signal)
      .then((stranicaOdgovora) => {
        setOdgovor({
          kljuc,
          radnici: stranicaOdgovora.items,
          ukupno: stranicaOdgovora.total,
        });
      })
      .catch((error: unknown) => {
        if (kontroler.signal.aborted) return;
        setGreska({ kljuc, poruka: porukaDohvata(error) });
        console.error(error);
      });

    return () => kontroler.abort();
  }, [kljuc, odgodenaPretraga, status, siteId]);

  const ponistiFiltre = useCallback(() => {
    setPretraga("");
    setStatus("aktivni");
    setSiteId("");
    setSamoUpozorenja(false);
  }, []);

  const prikazani = radnici
    .filter((radnik) =>
      samoUpozorenja
        ? oznakaDozvole(radnik.dozvola_status, radnik.vrsta_ugovora).ton !==
            "ok" ||
          oznakaLijecnickog(radnik.zdravstveni_status).ton !== "ok" ||
          oznakaUskladenosti(radnik.uskladenost).ton !== "ok"
        : true,
    );

  const filtriran =
    Boolean(odgodenaPretraga.trim()) || status !== "aktivni" || samoUpozorenja || !!siteId;

  return (
    <div className="avm-page avm-page--table">
      <PageHeader title="Zaposlenici" meta={<span>{ukupno} {status === "aktivni" ? "aktivnih zaposlenika" : "zaposlenika"}</span>} actions={<>{can("dozvola", "read") && <Link className="inline-flex min-h-11 items-center px-3 font-semibold underline" to="/dozvole/postupci">Dozvole u postupku</Link>}{can("radnik","create") && <><Link to="/zaposlenici/uvoz"><Button variant="secondary">Uvoz iz Excela</Button></Link><Link to="/zaposlenici/novi"><Button>Dodaj zaposlenika</Button></Link></>}</>} />

      <FilterBar>
        <label className="flex flex-col gap-2">
          <span className="oznaka">Pretraga</span>
          <input
            type="search"
            value={pretraga}
            onChange={(event) => setPretraga(event.target.value)}
            placeholder="Prezime, ime ili OIB"
            className="h-[var(--spacing-control)] w-72 rounded-[var(--radius-control)] border border-[var(--color-input-border)] bg-white px-3"
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="oznaka">Status</span>
          <DesignSelect
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as Status);
            }}
            className="h-[var(--spacing-control)] rounded-[var(--radius-control)] border border-[var(--color-input-border)] bg-white px-3"
          >
            <option value="aktivni">Aktivni</option>
            <option value="svi">Svi</option>
            <option value="bivsi">Bivši</option>
            <option value="povijesni">Povijesni</option>
          </DesignSelect>
        </label>

        {canReadSites && <label className="flex flex-col gap-2"><span className="oznaka">Gradilište</span><DesignSelect value={siteId} onChange={event => setSiteId(event.target.value)}><option value="">Sva gradilišta</option>{sites.map(site => <option key={site.id} value={site.id}>{site.naziv}</option>)}</DesignSelect>{siteError && <span role="alert">{siteError}</span>}</label>}

        <label className="flex h-[var(--spacing-control)] items-center gap-2">
          <input
            type="checkbox"
            checked={samoUpozorenja}
            onChange={(event) => setSamoUpozorenja(event.target.checked)}
            className="size-5 min-h-0"
          />
          <span>Samo s upozorenjima</span>
        </label>

        {filtriran ? (
          <Button variant="outline" onClick={ponistiFiltre}>
            Poništi filtre
          </Button>
        ) : null}
      </FilterBar>

      <Table>
        {greska?.kljuc === kljuc ? (
          <EmptyState
            naslov="Popis se ne može učitati"
            objasnjenje={greska.poruka}
            akcija={
              <Button onClick={() => setGreska(null)}>Pokušaj ponovno</Button>
            }
          />
        ) : ucitava ? (
          <SkeletonTablica redaka={3} />
        ) : prikazani.length === 0 ? (
          filtriran ? (
            <EmptyState
              naslov="Nema zaposlenika za odabrane filtre"
              objasnjenje="Promijenite status ili pretragu, ili poništite filtre."
              akcija={<Button onClick={ponistiFiltre}>Poništi filtre</Button>}
            />
          ) : (
            <EmptyState
              naslov="Još nema unesenih zaposlenika"
              objasnjenje="Zaposlenici se prikazuju čim ih kadrovska unese u evidenciju."
            />
          )
        ) : (
          <TablicaRadnika radnici={prikazani} />
        )}
      </Table>

    </div>
  );
}

function TablicaRadnika({ radnici }: { radnici: Radnik[] }) {
  return (
    <table className="w-full border-collapse text-left">
      <thead>
        <tr className="border-b border-[var(--color-border)]">
          <Zaglavlje>Prezime i ime</Zaglavlje>
          <Zaglavlje>OIB</Zaglavlje>
          <Zaglavlje>Radno mjesto</Zaglavlje>
          <Zaglavlje>Gradilište</Zaglavlje>
          <Zaglavlje>Dozvola</Zaglavlje>
          <Zaglavlje>Liječnički</Zaglavlje>
          <Zaglavlje>Usklađenost</Zaglavlje>
          <Zaglavlje>
            <span className="sr-only">Radnja</span>
          </Zaglavlje>
        </tr>
      </thead>
      <tbody>
        {radnici.map((radnik) => {
          const dozvola = oznakaDozvole(radnik.dozvola_status, radnik.vrsta_ugovora);
          const lijecnicki = oznakaLijecnickog(radnik.zdravstveni_status);
          const uskladenost = oznakaUskladenosti(radnik.uskladenost);
          const kriticno = kriticanRedak(
            radnik.dozvola_status,
            radnik.zdravstveni_status,
          );

          return (
            <tr
              key={radnik.id}
              className={cn(
                "h-[var(--spacing-row)] border-b border-[var(--color-border)] last:border-b-0",
                kriticno && "bg-[var(--color-bad-row)]",
              )}
            >
              <Celija>
                <Link
                  to={`/zaposlenici/${radnik.id}`}
                  className="font-medium underline-offset-2 hover:underline"
                >
                  <span className="inline-flex items-center gap-2"><Avatar name={punoIme(radnik)}/>{punoIme(radnik)}</span>
                </Link>
                <span className="ml-2 text-sm text-muted-foreground">{statusRadnika(radnik)}</span>
              </Celija>
              <Celija>
                <span className="broj">{radnik.oib ?? "—"}</span>
              </Celija>
              <Celija>{radnik.radno_mjesto ?? "—"}</Celija>
              <Celija>
                <span>{radnik.trenutni_raspored?.gradiliste ?? "—"}</span>
              </Celija>
              <Celija>
                <Badge ton={dozvola.ton}>{dozvola.tekst}</Badge>
              </Celija>
              <Celija>
                <Badge ton={lijecnicki.ton}>{lijecnicki.tekst}</Badge>
              </Celija>
              <Celija>
                <Link
                  to={`/zaposlenici/${radnik.id}?tab=osnovno`}
                  title={radnik.uskladenost?.nedostaje.join("\n") || "Usklađeno"}
                  className="inline-block"
                >
                  <Badge ton={uskladenost.ton}>{uskladenost.tekst}</Badge>
                </Link>
              </Celija>
              <Celija>
                <Link
                  to={`/zaposlenici/${radnik.id}`}
                  className="font-medium underline underline-offset-2"
                >
                  Otvori
                </Link>
              </Celija>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function Zaglavlje({ children }: { children: React.ReactNode }) {
  return <th className="oznaka px-4 py-3 font-semibold">{children}</th>;
}

function Celija({ children }: { children: React.ReactNode }) {
  return <td className="px-4">{children}</td>;
}


